import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpResponse, HttpStatusCode } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { EventService } from '@base/services/private/event.service';
import { SessionService } from '@base/services/private/session.service';
import { FaroService } from '@core/services/faro.service';
import { CookieService } from 'ngx-cookie-service';
import { catchError, map, of, throwError } from 'rxjs';

/**
 * HTTP interceptor that logs errors and (optionally) responses, excluding asset requests.
 *
 * Uses RxJS operators to handle and log HTTP events.
 *
 * @param req - The outgoing HTTP request.
 * @param next - The next handler or backend in the interceptor chain.
 * @returns Observable of HTTP events with error handling applied.
 */
export const httpResponseInterceptor: HttpInterceptorFn = (req, next) => {
  const sessionSrv = inject(SessionService);
  const router = inject(Router);
  const eventSrv = inject(EventService);
  const cookieSrv = inject(CookieService);
  const faroSrv = inject(FaroService);
  const eventDirPath = eventSrv?.data?.isBrandedDomain ? '' : `/${eventSrv?.data?.name}`;

  return next(req).pipe(
    map((event: HttpEvent<unknown>) => {
      if (event instanceof HttpResponse) {
        if (!req.url.includes('assets')) {
          // console.log('✅ HTTP Response:', event);
          setTimeout(() => {
            sessionSrv.removeItem('didReloadAfter403'); // after next req api is successfully then clear it as to make it as guard when other response is forbidden
          }, 1000);
        }
      }
      return event;
    }),
    catchError((error: HttpErrorResponse) => {
      const urlPath = req.url.split('?')[0];
      faroSrv.pushBackendError(error, req.method, urlPath);

      // Informa external endpoints return 401/403 for users not entitled to the MFE
      // (guests, non-Informa groups). That is an expected entitlement outcome, not an
      // invalid session — propagate it so callers surface their own error/failed state
      // instead of wiping the session and hard-reloading the page.
      const isInformaExternalEndpoint =
        urlPath.includes('auth/external-token') || urlPath.includes('tools/translate_external_id');
      if (isInformaExternalEndpoint) {
        return throwError(() => error);
      }

      if (error.status === HttpStatusCode.Forbidden || error.status === HttpStatusCode.Unauthorized) {
        const alreadyReloaded = sessionSrv.getItem('didReloadAfter403') === '1';
        // to avoid multiple hit api after 403
        if (!alreadyReloaded) {
          sessionSrv.clear();
          cookieSrv.deleteAll();
          sessionSrv.setItem('didReloadAfter403', '1');
          window.location.reload(); //TODO - will improve later
        }
        router.createUrlTree([eventDirPath]);
        return of();
      }

      return throwError(() => error);
    })
  );
};
